import React from "react";

import "../styles.scss";
import "../scripts";

import {
  BlockTheme,
  BlockType,
  getDefaultProps,
  KameleonElement,
  primaryBlock,
  PrimaryBlockConfig,
  renderSecondaryBlock,
  SecondaryBlockSize,
  TertiaryBlockSize,
  ThemeableBlock,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import {
  backgroundColorModeMapper,
  Variants,
} from "@kameleon-util/global-theme";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { ContentType, layoutOptions } from "./types/prop-types";

/** Content Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.Medium,
  tertiaryBlockSize: TertiaryBlockSize.None,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.light,
};

export const Content = primaryBlock<ContentType>(
  blockConfig,
  ({
    SecondaryBlock,
    tagline,
    header,
    description,
    cardHeader,
    cardSubtitle,
    price,
    period,
    items,
    layoutProps,
  }: ContentType) => {
    const currentThemeColor = backgroundColorModeMapper(
      layoutProps.backgroundColor
    ).color;

    const charactersExceeded = price.length > 5 || period.length > 5;
    const headerIsLeft = layoutProps.headerPlacement === "left";
    const cardIsRight = layoutProps.cardPlacement === "right";

    return (
      <Box className="primary-content-two-col-large-card-v1">
        <Box className="padding-global">
          <Box className="container-large">
            <Box className="padding-section-large">
              {(tagline || header || description) && (
                <Box
                  sx={{
                    textAlign: headerIsLeft ? "left" : "center",
                  }}
                  className="margin-bottom margin-xxlarge is-tablet-medium top-content"
                >
                  <Box className="max-width-large">
                    {tagline && (
                      <Box className="margin-bottom margin-xsmall">
                        <Variants.TagLine>{tagline}</Variants.TagLine>
                      </Box>
                    )}
                    {header && (
                      <Box className="margin-bottom margin-small">
                        <Variants.Heading>{header}</Variants.Heading>
                      </Box>
                    )}
                    {description && (
                      <Variants.ParagraphMedium>
                        {description}
                      </Variants.ParagraphMedium>
                    )}
                  </Box>
                </Box>
              )}
              <Box className="pricing5_component">
                <Box
                  sx={{ order: cardIsRight ? -1 : 1 }}
                  className="pricing5_content"
                >
                  <Box className="pricing5_content-list">
                    {items.map((item, index) => (
                      <Box
                        key={index}
                        id="pricing"
                        className="pricing5_content-item"
                      >
                        <Box className="pricing5_item-icon-wrapper">
                          {layoutProps.showIconBackground ? (
                            <Variants.IconSmall icon={item.icon} />
                          ) : (
                            <Variants.IconMedium
                              sx={{ width: "initial", height: "2.5rem" }}
                              hasBg={false}
                              icon={item.icon}
                            />
                          )}
                        </Box>
                        <Box className="pricing5_item-text-wrapper">
                          <Box className="margin-bottom margin-xsmall">
                            <Variants.Heading as="h4" variant="h6">
                              {item.header}
                            </Variants.Heading>
                          </Box>
                          <Variants.ParagraphDefault>
                            {item.description}
                          </Variants.ParagraphDefault>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <ThemeableBlock
                  blockTheme={BlockTheme.light}
                  blockType={BlockType.Primary}
                  isInnerBlock
                >
                  <Box sx={{ backgroundColor: currentThemeColor }}>
                    <Variants.Card className="pricing5_plan">
                      <Box className="pricing5_plan-content">
                        <Box
                          className={`pricing5_content-${
                            charactersExceeded ? "top" : "flex"
                          }`}
                        >
                          <Box
                            className={`pricing4_top${
                              charactersExceeded ? "" : "-left"
                            }`}
                          >
                            <Box className="margin-bottom margin-xxsmall">
                              <Variants.Heading as="h3" variant="h5">
                                {cardHeader}
                              </Variants.Heading>
                            </Box>
                            <Variants.ParagraphDefault>
                              {cardSubtitle}
                            </Variants.ParagraphDefault>
                          </Box>
                          <Box className="pricing4_top-right">
                            <Variants.Heading
                              as="h1"
                              className={`price price-size ${
                                !charactersExceeded && "no-wrap"
                              }`}
                            >
                              {price}
                            </Variants.Heading>
                            <Variants.Heading as="h4" className="price">
                              {period}
                            </Variants.Heading>
                          </Box>
                        </Box>
                        {SecondaryBlock && (
                          <Variants.DividerHorizontal className="pricing5_divider" />
                        )}
                        {renderSecondaryBlock(SecondaryBlock)}
                      </Box>
                    </Variants.Card>
                  </Box>
                </ThemeableBlock>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

Content.defaultProps = getDefaultProps<ContentType>(defaults);

export const Element: KameleonElement = {
  component: Content,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
